import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/shared/services/api.service';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UpgradeDialogComponent } from '../upgrade-dialog/upgrade-dialog.component';
import { RenewDialogComponent } from '../renew-dialog/renew-dialog.component';
import { SubscriptionDetailsDialogComponent } from '../subscription-details-dialog/subscription-details-dialog.component';
import { Router } from '@angular/router';
import { SubscriptionAccessService } from 'src/app/shared/services/subscription-access.service';

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.css']
})

export class SubscriptionComponent implements OnInit, OnDestroy {
  domains: any[] = [];
  subscriptions: any[] = [];
  loading = false;
  error: string | null = null;
  paymentGateway: 'stripe' | 'razorpay' = 'razorpay';
  /** JWT type 1 / role_id 1 — show all-org subscription list with organization names */
  isSuperAdmin = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder, 
    private paymentService: ApiService,
    private dialog: MatDialog, 
    private apiService: ApiService, 
    private router: Router,
    private subscriptionAccessService: SubscriptionAccessService
  ) {
    const userData = JSON.parse(localStorage.getItem("user") || '{}');
    const u = userData.user;
    this.domains = u?.domain?.split(",") || [];
    this.isSuperAdmin = u?.type === 1 || u?.role_id === 1;
  }

  ngOnInit(): void {
    this.getSubscriptionDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSubscriptionDetails() {
    this.loading = true;
    this.error = null;
    
    this.apiService.getSubscriptionDetails("")
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          console.log('Subscription details:', res);
          
          // Handle both single subscription and array of subscriptions
          if (Array.isArray(res)) {
            this.subscriptions = res;
          } else if (res) {
            this.subscriptions = [res];
          } else {
            this.subscriptions = [];
          }
          
          // Update days left for overview cards
          if (this.subscriptions.length > 0) {
            const firstSub = this.subscriptions[0];
            this.daysLeft = firstSub.statusDetails?.daysRemaining || 0;
            this.status = firstSub.status || 'Inactive';
          }
          
          this.loading = false;
        },
        error: (err) => {
          console.error('Error fetching subscription details:', err);
          this.error = 'Failed to load subscription details';
          this.loading = false;
        }
      });
  }

  billingCycle: 'Annual' | 'Monthly' = 'Annual';
  selectedPlan: any = 1; // Default selected

  setBillingCycle(value: 'Annual' | 'Monthly') {
    this.billingCycle = value;
  }

  selectPlan(plan: any) {
    this.selectedPlan = plan;
  }

  isSelected(plan: string): boolean {
    return this.selectedPlan == plan;
  }
  packageList: any;
  getPackage() {
    this.paymentService.getAllPackageDetails().subscribe({
      next: (res: any) => {
        this.packageList = res?.data;
      },
      error: (err) => {
        console.error('Error occurred:', err);
      },
      complete: () => {
        console.log('Packages fetch observable completed');
      }
    });
  }

  async subscribePackage(packageId: any) {
    this.loading = true;
    
    if (this.paymentGateway === 'razorpay') {
      this.subscribeWithRazorpay(packageId);
    } else {
      this.subscribeWithStripe(packageId);
    }
  }

  async subscribeWithStripe(packageId: any) {
    this.paymentService.subscribeToPackage(packageId).subscribe({
      next: async (response) => {
        console.log('Subscription successful', response);
        const clientSecret = response.clientSecret;

        const stripe = await loadStripe('pk_test_51P01nISHroK2oGqaxpHGQ3hpY2fyN32twitKHJijxBGQI9grqg0FTBlfQo8jbcTa2zLwvrUMnkMrfHeoDh3QPea100xOLMxzEu');
        if (stripe) {
          await stripe.redirectToCheckout({
            sessionId: clientSecret
          });
        } else {
          console.error('Stripe failed to initialize.');
          this.loading = false;
        }
      },
      error: (error) => {
        console.error('Subscription failed', error);
        this.loading = false;
      }
    });
  }

  subscribeWithRazorpay(packageData: any) {
    const userData = JSON.parse(localStorage.getItem("user") || '{}');
    
    // Handle both cases: packageId (number) or formattedData object from dialog
    let packageId: any;
    let totalAmount: number = 0;
    
    if (typeof packageData === 'object' && packageData !== null) {
      // It's formattedData from PackageDialogComponent
      packageId = packageData.packageId || packageData.id;
      totalAmount = packageData.total || 0;
    } else {
      // It's just packageId, need to fetch package details
      packageId = packageData;
      
      // Get package details to calculate amount
      this.apiService.getAllPackageDetails().subscribe({
        next: (packagesRes: any) => {
          const packages = packagesRes?.data || packagesRes || [];
          const selectedPackage = packages.find((pkg: any) => pkg.id === packageId);
          
          if (!selectedPackage) {
            console.error('Package not found');
            this.loading = false;
            return;
          }
          
          totalAmount = selectedPackage.total_amount || 0;
          this.processRazorpaySubscription(packageId, totalAmount, userData);
        },
        error: (error: any) => {
          console.error('Error fetching package details:', error);
          this.loading = false;
        }
      });
      return;
    }
    
    this.processRazorpaySubscription(packageId, totalAmount, userData);
  }

  processRazorpaySubscription(packageId: any, totalAmount: number, userData: any) {
    // Create subscription data similar to domain-selection
    const subscriptionData = {
      organization_id: userData.user?.branch_id || userData.user?.org_id,
      organization_code: userData.user?.branch_code || userData.user?.company_code,
      package_id: packageId,
      total_amount: totalAmount
    };

    // Create subscription first
    this.apiService.createSubscription(subscriptionData).subscribe({
      next: (subscriptionRes: any) => {
        console.log('Subscription created:', subscriptionRes);
        
        // Create Razorpay order
        this.apiService.createSubscriptionRazorpayOrder({
          amount: subscriptionData.total_amount,
          currency: 'INR',
          subscription_data: subscriptionData,
          subscription_id: subscriptionRes?.id || subscriptionRes?.subscription_id
        }).subscribe({
          next: (response: any) => {
            if (response.success && response.data) {
              const options = {
                key: response.data.key,
                amount: response.data.amount,
                currency: response.data.currency,
                name: 'SigmaTConflux Subscription',
                description: 'Package Subscription Fee',
                order_id: response.data.orderId,
                handler: (paymentResponse: any) => {
                  this.verifyRazorpayPayment(
                    paymentResponse, 
                    response.data.subscriptionId || subscriptionRes?.id || subscriptionRes?.subscription_id
                  );
                },
                prefill: {
                  name: userData.user?.full_name || '',
                  email: userData.user?.email_id || '',
                  contact: userData.user?.contact_number || ''
                },
                theme: {
                  color: '#3399cc'
                },
                modal: {
                  ondismiss: () => {
                    this.loading = false;
                    console.log('Payment cancelled');
                  }
                }
              };

              const razorpayInstance = new (window as any).Razorpay(options);
              razorpayInstance.open();
            } else {
              this.loading = false;
              console.error('Failed to create Razorpay order');
            }
          },
          error: (error: any) => {
            console.error('Error creating Razorpay order:', error);
            this.loading = false;
          }
        });
      },
      error: (error: any) => {
        console.error('Error creating subscription:', error);
        this.loading = false;
      }
    });
  }

  verifyRazorpayPayment(paymentResponse: any, subscriptionId: number) {
    this.apiService.verifySubscriptionRazorpayPayment(subscriptionId, {
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_signature: paymentResponse.razorpay_signature
    }).subscribe({
      next: (response: any) => {
        if (response.success) {
          console.log('Payment verified successfully');
          // ACC-004, ACC-007: Refresh subscription details and access after payment
          this.getSubscriptionDetails();
          this.subscriptionAccessService.refreshSubscriptionAccess();
          // Redirect to success page with payment_id and subscription_id
          this.router.navigate(['/home/success'], {
            queryParams: {
              payment_id: paymentResponse.razorpay_payment_id,
              subscription_id: subscriptionId
            }
          });
        } else {
          this.loading = false;
          console.error('Payment verification failed');
          this.error = 'Payment verification failed';
        }
      },
      error: (error: any) => {
        console.error('Error verifying payment:', error);
        this.loading = false;
        this.error = 'Error verifying payment';
      }
    });
  }

  // New methods for the redesigned subscription dashboard
  
  // Overview card methods - Updated to use backend data
  getTotalUsers(): number {
    return this.subscriptions.reduce((total: number, sub: any) => {
      let subTotal = 0;
      
      // Sum up users_count from all domain objects
      const domains = ['road', 'construction', 'education', 'utilities', 'consultancy'];
      domains.forEach(domain => {
        if (sub[domain] && sub[domain].users_count) {
          subTotal += sub[domain].users_count;
        }
      });
      
      return total + subTotal;
    }, 0);
  }

  getTotalAmount(): number {
    return this.subscriptions.reduce((total: number, sub: any) => total + (sub.total_amount || 0), 0);
  }

  // New method to get active subscriptions count
  getActiveSubscriptionsCount(): number {
    return this.subscriptions.filter((sub: any) => 
      sub.statusDetails?.isActive || sub.status === 'Active'
    ).length;
  }

  // New method to get days remaining for primary subscription
  getPrimaryDaysRemaining(): number {
    if (this.subscriptions.length > 0) {
      const primarySub = this.subscriptions[0];
      return primarySub.statusDetails?.daysRemaining || 0;
    }
    return 0;
  }

  // Status methods - Updated to use backend membership status
  getStatusClass(sub: any): string {
    const status = sub.status || this.getStatusText(sub);
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-active';
      case 'expired':
        return 'status-expired';
      case 'pending':
        return 'status-pending';
      case 'grace period':
        return 'status-grace';
      default:
        return 'status-inactive';
    }
  }

  getStatusIcon(sub: any): string {
    const status = sub.status || this.getStatusText(sub);
    switch (status.toLowerCase()) {
      case 'active':
        return 'check_circle';
      case 'expired':
        return 'cancel';
      case 'pending':
        return 'schedule';
      case 'grace period':
        return 'warning';
      default:
        return 'help';
    }
  }

  getStatusText(sub: any): string {
    // Use backend status if available, otherwise fallback to local calculation
    if (sub.status) {
      return sub.status;
    }
    
    // Fallback logic for backward compatibility
    if (sub.payment_status === 'Paid' && this.daysLeft > 0) {
      return 'Active';
    } else if (this.daysLeft <= 0) {
      return 'Expired';
    } else if (sub.payment_status === 'Pending') {
      return 'Pending';
    } else {
      return 'Inactive';
    }
  }

  // New method to get status details
  getStatusDetails(sub: any): any {
    return sub.statusDetails || {
      isActive: false,
      daysRemaining: 0,
      daysExpired: 0,
      gracePeriodDays: 7,
      gracePeriodRemaining: 0,
      canAccess: false,
      message: 'No status details available'
    };
  }

  // Payment status methods
  getPaymentStatusClass(sub: any): string {
    return sub.payment_status === 'Paid' ? 'payment-paid' : 'payment-pending';
  }

  getPaymentStatusIcon(sub: any): string {
    return sub.payment_status === 'Paid' ? 'check_circle' : 'schedule';
  }

  // Action methods
  showUpgradeOptions(sub?: any): void {
    const subscription = sub || (this.subscriptions.length > 0 ? this.subscriptions[0] : null);

    if (!subscription) {
      return;
    }

    const dialogRef = this.dialog.open(UpgradeDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        subscription,
        packages: []
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.getSubscriptionDetails();
        this.subscriptionAccessService.refreshSubscriptionAccess();
      }
    });
  }

  canRenew(sub?: any): boolean {
    if (this.isSuperAdmin) return false;
    const latest = this.subscriptions[0];
    const target = sub || latest;
    if (!target?.id || !latest?.id || target.id !== latest.id) return false;
    const status = String(target.status || this.getStatusText(target) || '').toLowerCase();
    return ['active', 'expired', 'grace period', 'pending'].includes(status);
  }

  showRenewOptions(sub?: any): void {
    const subscription = sub || this.subscriptions[0];
    if (!subscription?.id || !this.canRenew(subscription)) {
      return;
    }

    const dialogRef = this.dialog.open(RenewDialogComponent, {
      width: '480px',
      maxWidth: '90vw',
      data: { subscription }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.getSubscriptionDetails();
        this.subscriptionAccessService.refreshSubscriptionAccess();
      }
    });
  }

  viewDetails(sub: any): void {
    if (!sub) return;
    this.dialog.open(SubscriptionDetailsDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { subscription: sub },
      panelClass: 'subscription-details-dialog'
    });
  }

  hasInvoice(sub: any): boolean {
    const invoiceId = sub?.invoice_id ?? sub?.invoiceId ?? null;
    const subscriptionId = sub?.id ?? null;
    return (
      (invoiceId !== null && invoiceId !== undefined && String(invoiceId).trim() !== '') ||
      (subscriptionId !== null && subscriptionId !== undefined && String(subscriptionId).trim() !== '')
    );
  }

  private openInvoiceDialog(sub: any, invoice: any): void {
    this.dialog.open(SubscriptionDetailsDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        subscription: {
          ...sub,
          _invoiceView: true,
          invoice_no: invoice?.invoice_no ?? invoice?.invoiceNo ?? `SUB-${sub?.id ?? 'LOCAL'}`,
          total: invoice?.total ?? sub?.total_amount ?? 0,
          created_at: invoice?.created_at ?? sub?.created_at
        }
      },
      panelClass: 'subscription-details-dialog'
    });
  }

  private openLocalInvoiceFallback(sub: any): void {
    this.openInvoiceDialog(sub, {
      invoice_no: `SUB-${sub?.id ?? 'LOCAL'}`,
      total: sub?.total_amount ?? 0,
      created_at: sub?.created_at
    });
  }

  downloadInvoice(sub: any): void {
    if (!sub) return;
    this.error = null;
    const invoiceId = sub?.invoice_id ?? sub?.invoiceId ?? null;
    const subscriptionId = sub?.id ?? null;
    const lookupIds = [invoiceId, subscriptionId]
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
      .filter((v, idx, arr) => arr.indexOf(v) === idx);

    if (lookupIds.length === 0) {
      this.openLocalInvoiceFallback(sub);
      return;
    }

    const tryFetch = (index: number): void => {
      if (index >= lookupIds.length) {
        this.openLocalInvoiceFallback(sub);
        return;
      }

      this.apiService.getInvoiceById(lookupIds[index])
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const invoice = res?.invoice ?? res;
            if (!invoice) {
              tryFetch(index + 1);
              return;
            }
            this.openInvoiceDialog(sub, invoice);
          },
          error: () => {
            tryFetch(index + 1);
          }
        });
    };

    tryFetch(0);
  }

  manageSubscription(sub: any): void {
    console.log('Manage subscription:', sub.id);
    this.router.navigate(['/subscription-management']);
  }

  explorePackages(): void {
    console.log('Explore packages');
  }

  // Modal control
  showUpgradeModal = true;

  closeUpgradeModal(): void {
    this.showUpgradeModal = false;
  }

  openPackageDialog(data: any): void {
    const dialogRef = this.dialog.open(PackageDialogComponent, {
      width: '500px',
      data: data // or existing data for editing
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.subscribePackage(result)
      }
    });
  }

status="";
daysLeft=0;
  checkMembership(startDate:any,endDate:any) {
  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
// console.log(today >= start && today <= end);
  if (today >= start && today <= end) {
    this.status = 'Active';
    this.daysLeft = Math.ceil(
      (end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
  } else {
    this.status = 'Inactive';
    this.daysLeft = 0;
  }
}

  // ACC-003, ACC-004, ACC-005, ACC-006, ACC-007: Subscription management actions
  renewSubscription(subscription: any) {
    this.showRenewOptions(subscription);
  }

  suspendSubscription(subscription: any) {
    if (!subscription || !subscription.id) {
      this.error = 'Invalid subscription';
      return;
    }

    const reason = prompt('Enter reason for suspension:');
    if (!reason) return;

    this.loading = true;
    this.apiService.suspendSubscription(subscription.id, reason)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.getSubscriptionDetails();
            this.subscriptionAccessService.refreshSubscriptionAccess();
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error suspending subscription:', err);
          this.error = err.error?.message || 'Failed to suspend subscription';
          this.loading = false;
        }
      });
  }

  reactivateSubscription(subscription: any) {
    if (!subscription || !subscription.id) {
      this.error = 'Invalid subscription';
      return;
    }

    this.loading = true;
    this.apiService.reactivateSubscription(subscription.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            // ACC-006: Refresh access after reactivation
            this.getSubscriptionDetails();
            this.subscriptionAccessService.refreshSubscriptionAccess();
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error reactivating subscription:', err);
          this.error = err.error?.message || 'Failed to reactivate subscription';
          this.loading = false;
        }
      });
  }

  downgradeSubscription(subscription: any) {
    if (!subscription || !subscription.id) {
      this.error = 'Invalid subscription';
      return;
    }

    // Open downgrade dialog (similar to upgrade)
    // For now, just show a message
    const confirmDowngrade = confirm('Are you sure you want to downgrade this subscription? Some modules will be restricted.');
    if (!confirmDowngrade) return;

    // TODO: Open downgrade dialog with module selection
    this.error = 'Downgrade dialog not yet implemented. Please contact support.';
  }

  /** Licensed when users_count or projects is greater than zero. */
  domainHasEntitlement(field: unknown): boolean {
    if (!field || typeof field !== 'object') {
      return false;
    }
    const data = field as Record<string, unknown>;
    const users = Number(data['users_count']);
    if (Number.isFinite(users) && users > 0) {
      return true;
    }
    const projects = Number(data['projects']);
    return Number.isFinite(projects) && projects > 0;
  }

  consultancyDomainStats(sub: any): string {
    const c = sub?.consultancy;
    if (!c) {
      return '';
    }
    const parts: string[] = [];
    const projects = c.projects ?? sub.consultancy_projects;
    if (projects != null && Number(projects) > 0) {
      parts.push(`Projects: ${projects}`);
    }
    if (c.users_count != null) {
      parts.push(`Users: ${c.users_count}`);
    }
    return parts.join(' | ');
  }
}


export interface paymentModel {
  landSize: number;
  fsi: number;
  builtUpArea: number;
  duration: number;
  users: number;
  createdAt?: Date;
  updatedAt?: Date;
}


@Component({
  selector: 'app-angular-dialog',
  templateUrl: './sub-model.html',
  styleUrls: ['./sub-model.css']
})
export class PackageDialogComponent implements OnInit {
  form: FormGroup;
  unitArray = [
    { value: 'sqFeet', label: 'Square Feet' },
    { value: 'sqMeter', label: 'Square Meter' }
  ];
  durationOptions = [
    { label: 'Monthly', value: 30 },
    { label: 'Quarterly', value: 90 },
    { label: 'Half-Yearly', value: 180 },
    { label: 'Yearly', value: 360 },
  ];
  packages: any;
  subscriptions:any;
  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PackageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private apiService: ApiService
  ) { }
  totalPayAmount: any;
  packageId: any
  ngOnInit(): void {

    this.packageId = this.data.id

    this.apiService.getAllPackageDetails().subscribe({
      next: (res: any) => {
        this.packages = res.data;
      },
      error: (err) => {
        console.log(err);
      },
      complete: () => console.log('Observable complete'),
    });

    this.form = this.fb.group({
      land_size: ['', Validators.required],
      land_unit: ['sqFeet', Validators.required],
      fsi: ['2'],
      builtUpArea: ['', Validators.required],
      builtUpAreaUnit: ['sqFeet', Validators.required],
      duration: ['', Validators.required],
      users: ['', Validators.required]
    });
  }

  calculateAmount() {
    if (this.form.valid) {
      let builtUpArea = this.form.value.builtUpArea;
      const builtUpAreaUnit = this.form.value.builtUpAreaUnit; // 'sqFeet' or 'sqMeter'

      // Convert to standard unit (e.g., sqFeet for calculation)
      if (builtUpAreaUnit === 'sqMeter') {
        builtUpArea = builtUpArea * 10.7639; // convert to sqFeet
      }
      const subTotal =
        (this.data.sqr_amount * builtUpArea) +
        (this.data.per_user_price * this.form.value.users);

      this.totalPayAmount = subTotal * this.form.value.duration;
    }
  }
  onSubmit(): void {
    if (this.form.valid) {
      let builtUpArea = this.form.value.builtUpArea;
      const builtUpAreaUnit = this.form.value.builtUpAreaUnit; // 'sqFeet' or 'sqMeter'

      // Convert to standard unit (e.g., sqFeet for calculation)
      if (builtUpAreaUnit === 'sqMeter') {
        builtUpArea = builtUpArea * 10.7639; // convert to sqFeet
      }
      const subTotal =
        (this.data.sqr_amount * builtUpArea) +
        (this.data.per_user_price * this.form.value.users);

      const total = subTotal * this.form.value.duration;

      const formattedData = {
        landSize: this.form.value.land_size,  // single combined field
        fsi: this.form.value.fsi,
        builtUpArea: this.form.value.builtUpArea,
        builtUpAreaUnit: this.form.value.builtUpAreaUnit,
        duration: this.form.value.duration,
        users: this.form.value.users,
        total: total,
        packageId: this.packageId
      };
      this.dialogRef.close(formattedData);
    }
  }
  onPackageSelect(pkg: any) {
    this.data = pkg;
    this.packageId = pkg?.id;
    console.log('Selected Package:', pkg);
    // Optionally patch form values or recalculate price
    this.calculateAmount();
  }


}

